import React, { useState } from "react";
import { Check, Loader2, Eye, EyeOff } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import toast from "react-hot-toast";

const NewPasswordScreen = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!hasMinLength || !hasSpecialChar) {
      setError("Password doesn't meet the requirements below.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Reset link is invalid or missing a token.");
      return;
    }

    setLoading(true);
    try {
        console.log("token:", token, "password:", password);

      await apiService.resetPassword({ token, password });
      toast.success("Password reset successfully. Please log in.");
      navigate("/login");
    } catch (err) {
      console.error("Reset password error:", err);
      setError(
        err.response?.data?.message || "Reset link is invalid or has expired."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <img src="/assets/clockout-lockup-hero-2400.png" alt="Clock-out" className="auth-logo" />
      <div className="w-full max-w-[440px]">
        {/* Card Container */}
        <div className="auth-card">
          {/* Header */}
          <div className="text-center mb-7">
            <h1 className="auth-title">
              Set new password
            </h1>
            <p className="auth-subtitle">
              Your new password must be different to previously used passwords.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-[12px]" role="alert">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password Field */}
            <div>
              <label className="auth-label">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth-input pr-12"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#857A63] hover:text-[#1C1A14] transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="auth-label">
                Confirm password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="auth-input pr-12"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#857A63] hover:text-[#1C1A14] transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-600 mt-2">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="space-y-2 py-1">
              <div className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    hasMinLength ? "bg-[#1C1A14]" : "bg-[#E3D6B6]"
                  }`}
                >
                  {hasMinLength && <Check size={11} className="text-[#FFC81E]" />}
                </div>
                <span
                  className={`text-sm ${
                    hasMinLength ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  Must be at least 8 characters
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    hasSpecialChar ? "bg-[#1C1A14]" : "bg-[#E3D6B6]"
                  }`}
                >
                  {hasSpecialChar && <Check size={11} className="text-[#FFC81E]" />}
                </div>
                <span
                  className={`text-sm ${
                    hasSpecialChar ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  Must contain one special character
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !hasMinLength || !hasSpecialChar || !passwordsMatch}
              className="auth-btn-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Resetting…
                </>
              ) : (
                "Reset password"
              )}
            </button>
          </form>

        </div>
      </div>

      {/* Footer */}
      <p className="auth-footer">
        © {new Date().getFullYear()} Talk Nerdy To Me Consultancy Ltd. All rights reserved.
      </p>
    </div>
  );
};

export default NewPasswordScreen;